import { ShapeHandler } from "./shape-handler.interface";
import { Annotation } from "../models/annotation.model";

/**
 * Handler for rectangle-specific behavior
 * Implements the ShapeHandler interface for rectangles
 */
export class RectangleHandler implements ShapeHandler {
  /**
   * Gets the left position (percentage) of a rectangle annotation
   */
  getLeft(annotation: Annotation): number {
    return (annotation as any).x;
  }

  /**
   * Gets the top position (percentage) of a rectangle annotation
   */
  getTop(annotation: Annotation): number {
    return (annotation as any).y;
  }

  /**
   * Gets the width (percentage) of a rectangle annotation
   */
  getWidth(annotation: Annotation): number {
    return (annotation as any).width;
  }

  /**
   * Gets the height (percentage) of a rectangle annotation
   */
  getHeight(annotation: Annotation): number {
    return (annotation as any).height;
  }

  /**
   * Gets the padding-bottom value for rectangles (always null)
   */
  getPaddingBottom(annotation: Annotation): number | null {
    return null; // Rectangles don't use padding-bottom
  }

  /**
   * Updates a rectangle during drag operations based on the drag mode
   */
  updateOnDrag(
    dragMode: string,
    annotation: any,
    dragStart: {
      x?: number;
      y?: number;
      width?: number;
      height?: number;
    },
    deltaXPercent: number,
    deltaYPercent: number,
    minSizePercent: number
  ): any {
    const result = { ...annotation };

    // Apply the appropriate transformation based on drag mode
    switch (dragMode) {
      case "whole":
        // Move the entire rectangle
        result.x = Math.max(0, Math.min(100 - (dragStart.width || 0), (dragStart.x || 0) + deltaXPercent));
        result.y = Math.max(0, Math.min(100 - (dragStart.height || 0), (dragStart.y || 0) + deltaYPercent));
        break;

      case "tl": // Top left corner drag - resize from top left
        {
          // Calculate new position and size
          const newX = Math.min(
            (dragStart.x || 0) + deltaXPercent,
            (dragStart.x || 0) + (dragStart.width || 0) - minSizePercent
          );
          const newY = Math.min(
            (dragStart.y || 0) + deltaYPercent,
            (dragStart.y || 0) + (dragStart.height || 0) - minSizePercent
          );
          const newWidth = Math.max(minSizePercent, (dragStart.width || 0) - deltaXPercent);
          const newHeight = Math.max(minSizePercent, (dragStart.height || 0) - deltaYPercent);

          // Update result
          result.x = newX;
          result.y = newY;
          result.width = newWidth;
          result.height = newHeight;
        }
        break;

      case "tr": // Top right corner drag - resize from top right
        {
          // Calculate new position and size
          const newY = Math.min(
            (dragStart.y || 0) + deltaYPercent,
            (dragStart.y || 0) + (dragStart.height || 0) - minSizePercent
          );
          const newWidth = Math.max(minSizePercent, (dragStart.width || 0) + deltaXPercent);
          const newHeight = Math.max(minSizePercent, (dragStart.height || 0) - deltaYPercent);

          // Update result
          result.y = newY;
          result.width = newWidth;
          result.height = newHeight;
        }
        break;

      case "bl": // Bottom left corner drag - resize from bottom left
        {
          // Calculate new position and size
          const newX = Math.min(
            (dragStart.x || 0) + deltaXPercent,
            (dragStart.x || 0) + (dragStart.width || 0) - minSizePercent
          );
          const newWidth = Math.max(minSizePercent, (dragStart.width || 0) - deltaXPercent);
          const newHeight = Math.max(minSizePercent, (dragStart.height || 0) + deltaYPercent);

          // Update result
          result.x = newX;
          result.width = newWidth;
          result.height = newHeight;
        }
        break;

      case "br": // Bottom right corner drag - resize from bottom right
        {
          // Calculate new size
          const newWidth = Math.max(minSizePercent, (dragStart.width || 0) + deltaXPercent);
          const newHeight = Math.max(minSizePercent, (dragStart.height || 0) + deltaYPercent);

          // Update result
          result.width = newWidth;
          result.height = newHeight;
        }
        break;

      default:
        console.warn(`Unknown drag mode: ${dragMode}`);
        break;
    }

    // Ensure rectangle stays within bounds (0-100%)
    result.x = Math.max(0, Math.min(100 - result.width, result.x));
    result.y = Math.max(0, Math.min(100 - result.height, result.y));
    result.width = Math.max(minSizePercent, Math.min(100, result.width));
    result.height = Math.max(minSizePercent, Math.min(100, result.height));

    return result;
  }

  /**
   * Converts a rectangle annotation to points array for storage
   */
  toPoints(annotation: any): { x: number; y: number }[] {
    return [
      { x: annotation.x, y: annotation.y }, // Top-left
      { x: annotation.x + annotation.width, y: annotation.y + annotation.height } // Bottom-right
    ];
  }
}
